import PageHeader from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Key, Zap, Bell, Users, Palette } from "lucide-react";

const INTEGRATIONS = [
  { name: "OpenAI",    status: "connected", key: "sk-••••••••3fA2", cat: "AI" },
  { name: "Claude",    status: "connected", key: "sk-••••••••8Kx1", cat: "AI" },
  { name: "Perplexity", status: "disconnected", key: null,          cat: "AI" },
  { name: "Hunter.io", status: "connected", key: "hn-••••••••b8",   cat: "Enrichment" },
  { name: "Apollo",    status: "disconnected", key: null,           cat: "Enrichment" },
  { name: "Gmail",     status: "connected", key: "OAuth · sales@b2b.agency", cat: "Email" },
  { name: "Outlook",   status: "disconnected", key: null,           cat: "Email" },
  { name: "n8n",       status: "connected", key: "http://n8n.b2b.internal", cat: "Automation" },
  { name: "Slack",     status: "connected", key: "#rfp-alerts",     cat: "Notify" },
  { name: "Google Maps API", status: "disconnected", key: null,     cat: "Data" },
];

export default function Settings() {
  return (
    <div data-testid="settings-page">
      <PageHeader
        eyebrow="Settings"
        title="Wire it up. Make it yours."
        description="Manage API keys, workflow triggers, team access and notifications for the entire OS."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Integrations */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Integrations</div>
                <h2 className="font-display text-xl text-white">API keys & providers</h2>
              </div>
              <Button variant="ghost" size="sm" className="text-white/60 rounded-full border border-white/[0.06]">
                <Key className="h-3.5 w-3.5 mr-1.5" /> Add key
              </Button>
            </div>
            <div className="divide-y divide-white/[0.05]">
              {INTEGRATIONS.map((i) => (
                <div key={i.name} data-testid={`settings-integration-${i.name.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-4 py-3">
                  <div className="h-9 w-9 rounded-lg bg-white/[0.03] border border-white/[0.06] grid place-items-center text-indigo-300">
                    <Zap className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] text-white">{i.name}</span>
                      <Badge className="rounded-full bg-white/[0.03] border border-white/[0.06] text-white/50 font-mono text-[10px] uppercase tracking-wider hover:bg-white/[0.03]">
                        {i.cat}
                      </Badge>
                    </div>
                    <div className="font-mono text-[11.5px] text-white/40 mt-0.5">
                      {i.key || "Not connected"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      i.status === "connected"
                        ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20"
                        : "bg-white/[0.03] text-white/40 border border-white/[0.06]"
                    }`}>
                      {i.status}
                    </span>
                    <Switch defaultChecked={i.status === "connected"} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Workflow triggers */}
          <div className="glass-card p-6">
            <div className="mb-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Automation</div>
              <h2 className="font-display text-xl text-white">Global triggers</h2>
            </div>
            <div className="space-y-3">
              {[
                { label: "Auto-generate proposal for jobs with match ≥ 90%",      on: true },
                { label: "Slack alert on new RFP > $1M budget",                     on: true },
                { label: "Send outreach at 08:00 local time for each recipient",   on: true },
                { label: "Pause a sequence if the lead replies",                    on: true },
                { label: "Re-scan Maps radius every 24 hours",                      on: false },
              ].map((t, i) => (
                <div key={i} className="flex items-center gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3">
                  <Zap className="h-4 w-4 text-cyan-300" />
                  <div className="flex-1 text-[13.5px] text-white/80">{t.label}</div>
                  <Switch defaultChecked={t.on} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          <div className="glass-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-4 w-4 text-indigo-300" />
              <div className="font-display text-lg text-white">Team</div>
            </div>
            <div className="space-y-3">
              {[
                { n: "Rahul Verma",  r: "Founder",      i: "RV" },
                { n: "Priya Singh",  r: "Sales Lead",   i: "PS" },
                { n: "Deepak Kumar", r: "Ops",          i: "DK" },
                { n: "AI Copilot",   r: "Automation",   i: "AI", ai: true },
              ].map((m) => (
                <div key={m.n} className="flex items-center gap-3">
                  <div className={`h-9 w-9 rounded-full grid place-items-center font-display font-semibold text-sm ${
                    m.ai ? "bg-gradient-to-br from-indigo-500 to-cyan-400" : "bg-white/[0.05] border border-white/[0.06]"
                  }`}>
                    {m.i}
                  </div>
                  <div className="flex-1">
                    <div className="text-[13.5px] text-white">{m.n}</div>
                    <div className="text-[11.5px] text-white/50">{m.r}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Bell className="h-4 w-4 text-cyan-300" />
              <div className="font-display text-lg text-white">Notifications</div>
            </div>
            <div className="space-y-3 text-[13px]">
              {["Slack · #rfp-alerts", "Email digest · daily 9am", "SMS · closing < 24h", "AI weekly summary"].map((l) => (
                <div key={l} className="flex items-center justify-between">
                  <span className="text-white/70">{l}</span>
                  <Switch defaultChecked />
                </div>
              ))}
            </div>
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Palette className="h-4 w-4 text-pink-300" />
              <div className="font-display text-lg text-white">Appearance</div>
            </div>
            <div className="space-y-3 text-[13px]">
              <div className="flex items-center justify-between">
                <span className="text-white/70">Dark mode</span>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/70">Reduced motion</span>
                <Switch />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/70">Compact mode</span>
                <Switch />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
